/**
 * React Flow diagram component for process recording
 * Uses measurement-based positioning for accurate alignment
 */
import { useRef, useEffect, useLayoutEffect, useState, useCallback } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  useNodesState,
  useEdgesState,
  useReactFlow,
  useNodesInitialized,
  useStore,
  ReactFlowProvider,
  type Node,
  type Edge,
  type ReactFlowState
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import '../recording-canvas.css';
import { nodeTypes } from '../utils/layoutUtils';
import { LAYOUT_CONSTANTS } from '@/constants/layoutConstants';
import type { SuccessorsColumnNodeData } from '../nodes/SuccessorsColumnNode';
import { computeFocusViewport, MIN_ZOOM, MAX_ZOOM } from '../utils/computeFocusViewport';

// Selector to get node count from React Flow's internal store
const nodeCountSelector = (state: ReactFlowState) => state.nodeLookup.size;

// Selector to get the successors column measured height from the store
const successorsHeightSelector = (state: ReactFlowState) => {
  const node = state.nodeLookup.get('successors-column');
  return node?.measured?.height ?? 0;
};

// Selector to get the target handle's Y offset within the successors column node
// The handle is positioned at the measured center of the cards
const successorsHandleYSelector = (state: ReactFlowState) => {
  const node = state.nodeLookup.get('successors-column');
  const targetHandles = node?.internals?.handleBounds?.target;
  if (targetHandles && targetHandles.length > 0) {
    // Handle position is relative to node top-left, we want center of handle
    return targetHandles[0].y + targetHandles[0].height / 2;
  }
  // Fallback to center of node if handle not measured yet
  return node?.measured?.height ? node.measured.height / 2 : 0;
};

interface ProcessDiagramProps {
  /**
   * Initial nodes to display in the diagram (pre-positioned by getLayoutedElements)
   */
  initialNodes: Node[];

  /**
   * Initial edges connecting the nodes
   */
  initialEdges: Edge[];

  /**
   * Content signature for tracking when diagram should update
   */
  contentSignature: string;

  /**
   * Pixels at the left of the canvas covered by an overlay (the Bliksy panel).
   * The focused step is centered in the region to the right of this inset and
   * fit to that region, so the panel never covers it. The narrower region also
   * zooms the view out a little. 0 when nothing overlays the canvas.
   */
  leftInset?: number;
}

/**
 * Inner component that handles the diagram logic
 * Must be inside ReactFlowProvider to use React Flow hooks
 */
function ProcessDiagramInner({
  initialNodes,
  initialEdges,
  contentSignature,
  leftInset = 0
}: ProcessDiagramProps) {
  const [nodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);
  const previousSignatureRef = useRef<string>('');

  // Measurement-based positioning using React Flow's internal store
  const { getNodes, getNodesBounds, setCenter } = useReactFlow();
  const nodesInitialized = useNodesInitialized();
  const storeNodeCount = useStore(nodeCountSelector);
  const successorsHeight = useStore(successorsHeightSelector);
  const successorsHandleY = useStore(successorsHandleYSelector);
  // Pane dimensions drive the focus calculation below.
  const paneWidth = useStore((s) => s.width);
  const paneHeight = useStore((s) => s.height);
  // Track if we've positioned (for initial fade-in)
  const [hasPositioned, setHasPositioned] = useState(false);

  useEffect(
    function syncNodesAndEdgesWithContent() {
      if (contentSignature !== previousSignatureRef.current && initialNodes.length > 0) {
        previousSignatureRef.current = contentSignature;

        const nodeMap = new Map(initialNodes.map((n) => [n.id, n]));

        setNodes((currentNodes) => {
          if (currentNodes.length !== initialNodes.length) {
            return initialNodes;
          }

          return currentNodes.map((node) => {
            const newNode = nodeMap.get(node.id);
            if (newNode) {
              // Preserve successors-column Y position - it's managed by positioning effect
              const preservePosition = node.id === 'successors-column';
              return {
                ...node,
                data: newNode.data,
                type: newNode.type,
                position: preservePosition ? node.position : newNode.position
              };
            }
            return node;
          });
        });

        setEdges(initialEdges);
      }
    },
    [contentSignature, initialNodes, initialEdges, setNodes, setEdges]
  );

  useEffect(
    function positionSuccessorsColumn() {
      // Wait for measurements to be ready
      if (
        !nodesInitialized ||
        storeNodeCount === 0 ||
        successorsHeight === 0 ||
        successorsHandleY === 0
      ) {
        return;
      }

      const measuredNodes = getNodes();
      const successorsNode = measuredNodes.find((n) => n.id === 'successors-column');
      const stepNode = measuredNodes.find((n) => n.id === 'step-with-who-1');

      if (successorsNode?.measured?.height && stepNode) {
        const nodeData = successorsNode.data as unknown as SuccessorsColumnNodeData;
        const successorCount = nodeData?.successors?.length || 1;
        const isDecision = successorCount > 1;

        let newY: number;
        if (isDecision) {
          // For decisions: align handles between step and successors column
          const stepHandleY = stepNode.position.y + LAYOUT_CONSTANTS.DECISION_STEP_HANDLE_OFFSET;
          newY = stepHandleY - successorsHandleY;
        } else {
          // For tasks: align top with preview
          newY = LAYOUT_CONSTANTS.PREVIEW_Y;
        }

        setNodes((currentNodes) =>
          currentNodes.map((n) =>
            n.id === 'successors-column' ? { ...n, position: { ...n.position, y: newY } } : n
          )
        );
        setHasPositioned(true);
      }
    },
    [nodesInitialized, storeNodeCount, successorsHeight, successorsHandleY, getNodes, setNodes]
  );

  // Focus the active step in the region right of any left overlay (Bliksy).
  // React Flow's own fitView centers within the full pane and only honors
  // asymmetric padding when the horizontal axis is the binding constraint, so a
  // tall step ends up centered behind the panel. We instead compute the viewport
  // directly (see computeFocusViewport) and shift it right of the inset. The
  // React Flow viewport is an imperative external surface, so syncing it to
  // layout state (inset, pane size) belongs in a layout effect — run pre-paint
  // to avoid a flash, animated only when the panel toggles.
  const wasOpenRef = useRef(leftInset > 0);
  // Once the user pans/zooms the canvas themselves, the view is theirs: stop
  // auto-fitting so we don't snap away their adjustment. React Flow passes the
  // originating event for user gestures and null for our own programmatic moves,
  // so we can tell them apart without masking.
  const userAdjustedRef = useRef(false);
  const handleMoveStart = useCallback((event: MouseEvent | TouchEvent | null) => {
    if (event) userAdjustedRef.current = true;
  }, []);

  useLayoutEffect(
    function focusViewportOnActiveStep() {
      if (!hasPositioned || paneWidth === 0 || paneHeight === 0) return;
      if (userAdjustedRef.current) return;

      const allNodes = getNodes();
      const stepNode = allNodes.find((n) => n.id === 'step-with-who-1');
      const stepWidth = stepNode?.measured?.width ?? 0;
      const stepHeight = stepNode?.measured?.height ?? 0;
      if (!stepNode || stepWidth === 0 || stepHeight === 0) return;

      const { x, y, zoom } = computeFocusViewport({
        paneWidth,
        paneHeight,
        leftInset,
        step: {
          x: stepNode.position.x,
          y: stepNode.position.y,
          width: stepWidth,
          height: stepHeight
        },
        bounds: getNodesBounds(allNodes)
      });

      // Glide only when the panel opens/closes (the overlay appears/disappears).
      // Resize drags and window resizes change the inset between non-zero values
      // and should track instantly rather than lag behind a 300ms tween.
      const nowOpen = leftInset > 0;
      const duration = wasOpenRef.current !== nowOpen ? 300 : 0;
      wasOpenRef.current = nowOpen;
      void setCenter(x, y, { zoom, duration });
    },
    [hasPositioned, leftInset, paneWidth, paneHeight, getNodes, getNodesBounds, setCenter]
  );

  return (
    <div className="h-full bg-bg-5">
      <div
        className="h-full"
        style={{
          opacity: hasPositioned ? 1 : 0,
          transition: 'opacity 0.15s ease-in'
        }}
      >
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onMoveStart={handleMoveStart}
          nodeTypes={nodeTypes}
          proOptions={{ hideAttribution: true }}
          className="recording-canvas"
          minZoom={MIN_ZOOM}
          maxZoom={MAX_ZOOM}
          zoomOnScroll
          preventScrolling={false}
          panOnScroll
          panOnDrag
          nodesDraggable={false}
          nodesConnectable={false}
          // Read-only canvas: React Flow's default Backspace-delete would
          // remove selected nodes (e.g. the successors column) client-side
          // only, so keyboard deletion is disabled outright.
          deleteKeyCode={null}
          snapToGrid
          snapGrid={[20, 20]}
        >
          <Background color="#aaa" gap={16} size={1} />
          {/* Zoom +/- only; fit-view and the interactive lock aren't useful here. */}
          <Controls position="top-right" showFitView={false} showInteractive={false} />
        </ReactFlow>
      </div>
    </div>
  );
}

/**
 * Process recording flow diagram component
 * Wraps React Flow with proper state management
 */
export function ProcessDiagram(props: ProcessDiagramProps) {
  return (
    <ReactFlowProvider>
      <ProcessDiagramInner {...props} />
    </ReactFlowProvider>
  );
}
